/**
 * Reset Password page
 */
import { useState } from 'react'
import AuthLayout from '../../components/AuthLayout/AuthLayout';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import Button from '../../components/Button';
import Link from '@mui/material/Link';
import { RESET_PASSWORD } from '../../constants/api';
import api from '../../services/api';
import { Link as RouterLink } from "react-router-dom";
import { Navigate, useParams } from "react-router-dom";
import { ROUTES } from '../../constants/routes'

function ResetPassword() {
  const [formData, setFormData] = useState({});
  const [apiCallInProgress, setApiCallInProgress] = useState(false);
  const [showInfo, setShowInfo] = useState(null);
  const [submitDisabled, setSubmitDisabled] = useState(true);
  const [passwordsDontMatch, setPasswordsDontMatch] = useState(false);
  const [highlightPoints, setHighlightPoints] = useState({valid1: false, valid2: false});
  const params = useParams();
  const { uidb64, token } = params;
  if (!uidb64 || !token) {
    return (<Navigate replace to={ROUTES.login} />);
  }

  async function resetPassword(data) {
    try {
      const response = await api.post(RESET_PASSWORD, data);
      ((response.data.data.status).toLowerCase() === 'success' && response.data.success) ? setShowInfo('success') : setShowInfo('failed');
      setApiCallInProgress(false);
      } catch (error) {
      console.error(error);
      setShowInfo('failed');
    }
  }

  function validatePassword() {
    const checkPassword = (pass) => /.{2,}/.test(pass) ? true : false;
    
    if (checkPassword(formData.new_password1) && (formData.new_password1 === formData.new_password2)) {
      setApiCallInProgress(true);
      resetPassword({...formData, token: token, uidb64: uidb64});
    } else {
      setPasswordsDontMatch(true);
    }
  };

  function arePasswordsValid(password1, password2) {
  return (
      (typeof password1 === 'string' && password1.trim() !== '') &&
      (typeof password2 === 'string' && password2.trim() !== '')
    );
  }

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    const passwordPattern = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+[\]{}|;:'",.<>?/\\])[\S]+$/;
    const isValidPassword = passwordPattern.test(value) && value.length >= 8;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
    if (arePasswordsValid(formData.new_password1, formData.new_password2)) setSubmitDisabled(!isValidPassword);
    setHighlightPoints({valid2: passwordPattern.test(value), valid1: value.length >= 8})
  };

  return (
    <AuthLayout title={'Reset Password'}>
    { showInfo === null ?
      <>
      <Typography style={{ margin: '15px' }}>
        You can update your password by entering your new password below.
        Once you have updated the password, you would be able to log in with the new password.
      </Typography>
      <Typography style={{ margin: '0 15px', fontWeight: '400' }} variant={"subtitle2"} color={highlightPoints.valid1 ? "" : "error"}>
        Password should contain atleast 8 characters <sup>*</sup>
      </Typography>
      <Typography style={{ margin: '0 15px', fontWeight: '400' }} variant={"subtitle2"} color={highlightPoints.valid2 ? "" : "error"}>Password must contain at least one uppercase letter, one lowercase letter, one numeric digit, and one special character.<sup>*</sup></Typography>
      <TextField
        variant="outlined"
        margin="normal"
        required
        fullWidth
        name="new_password1"
        label="New Password"
        type="password"
        id="password"
        autoComplete="new-password"
        onChange={handleInputChange}
      />
      <TextField
        variant="outlined"
        margin="normal"
        required
        fullWidth
        name="new_password2"
        label="Confirm Password"
        type="password"
        id="confirmPassword"
        autoComplete="confirm-new-password"
        onChange={handleInputChange}
      />
      <Button
        type="submit"
        fullWidth
        variant="contained"
        color="primary"
        sx={{margin: '24px 0 16px'}}
        onClick={validatePassword}
        disabled={submitDisabled}
      >
      {apiCallInProgress ? <CircularProgress size={24} color="secondary" /> : 'RESET PASSWORD'}
      </Button>
      { passwordsDontMatch &&
        <Typography variant="body2" textAlign="center" sx={{margin: '16px 0'}} color="error">
          <>New Password and Confirm Password doesn't match. Please try again.</>
        </Typography>
      }
      </>
      : showInfo === 'success' ? 
          <>Password has been changed successfully. Please <Link component={RouterLink} to={ROUTES.login} variant="body2">click here to login</Link> using your new password.</> : <>Could not update your password. Please try to <Link component={RouterLink} underline="always" to={ROUTES.forgotPassword} variant="body2">request a new password</Link> or contact support.</>
      }
    </AuthLayout>
  )
}

export default ResetPassword
